import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

type Tab = 'accounts' | 'journals' | 'trial' | 'fixed' | 'payroll' | 'partners' | 'vat' | 'reports' | 'ebalance' | 'openclose';

interface Account {
  id: string; code: string; name: string; parent_id: string | null;
  account_type: string; normal_balance: string; is_leaf: boolean; is_active: boolean;
}
interface JournalLine { id?: string; account_id: string; debit: number; credit: number; description?: string; partner_id?: string; }
interface Journal {
  id: string; journal_type: string; doc_no: string; doc_date: string; description: string;
  status: string; total_debit: number; total_credit: number; lines: JournalLine[];
}
interface TrialRow { account_id: string; code: string; name: string; debit: number; credit: number; balance: number; }

const TYPE_MN: Record<string, string> = {
  asset: 'Хөрөнгө', liability: 'Өр төлбөр', equity: 'Эздийн өмч', income: 'Орлого', expense: 'Зардал',
};
const JTYPE_MN: Record<string, string> = {
  cash: 'Касс', bank: 'Харилцах', purchase: 'Худалдан авалт', sale: 'Борлуулалт',
  inventory: 'Бараа материал', fixed_asset: 'Үндсэн хөрөнгө', payroll: 'Цалин',
  general: 'Ерөнхий', closing: 'Хаалт',
};
const STATUS_MN: Record<string, string> = { draft: 'Ноорог', posted: 'Батлагдсан', reversed: 'Залруулсан' };
const fmt = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AccountingPage() {
  const [tab, setTab] = useState<Tab>('accounts');

  return (
    <div>
      <div style={headerBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={logoBox}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div style={{ display: 'flex', gap: 0 }}>
            {(['accounts', 'journals', 'trial', 'partners', 'fixed', 'payroll', 'vat', 'reports', 'openclose', 'ebalance'] as Tab[]).map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                ...tabBtn,
                color: tab === t ? '#fff' : 'rgba(255,255,255,.6)',
                fontWeight: tab === t ? 700 : 400,
                borderBottom: tab === t ? '2px solid #fff' : '2px solid transparent',
              }}>
                {t === 'accounts' ? 'Дансны төлөвлөгөө' : t === 'journals' ? 'Журнал (гүйлгээ)'
                  : t === 'trial' ? 'Гүйлгээ баланс' : t === 'partners' ? 'Харилцагч'
                  : t === 'fixed' ? 'Үндсэн хөрөнгө' : t === 'payroll' ? 'Цалин' : t === 'vat' ? 'НӨАТ'
                  : t === 'reports' ? 'Санхүүгийн тайлан' : t === 'openclose' ? 'Нээлт/Хаалт' : 'E-Balance'}
              </button>
            ))}
          </div>
        </div>
        <div style={versionBadge}>
          <span>Нягтлан бодох бүртгэл</span>
          <span style={{ fontWeight: 700, marginLeft: 8 }}>Давхар бичилт</span>
        </div>
      </div>

      <div style={{ padding: 20 }}>
        {tab === 'accounts' && <AccountsTab />}
        {tab === 'journals' && <JournalsTab />}
        {tab === 'trial' && <TrialBalanceTab />}
        {tab === 'fixed' && <FixedAssetsTab />}
        {tab === 'payroll' && <PayrollTab />}
        {tab === 'reports' && <ReportsTab />}
        {tab === 'partners' && <PartnersTab />}
        {tab === 'vat' && <VatTab />}
        {tab === 'openclose' && <OpenCloseTab />}
        {tab === 'ebalance' && <EBalanceTab />}
      </div>
    </div>
  );
}

// ─── Дансны төлөвлөгөө ───
function AccountsTab() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try { setAccounts((await api.get('/admin/accounting/accounts')).items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setMsg('');
    try {
      const r = await api.post('/admin/accounting/accounts/seed-standard');
      setMsg(r.message);
      await load();
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Дансны төлөвлөгөө</h3>
        {accounts.length === 0 && (
          <button style={primaryBtn} onClick={seed}>Стандарт төлөвлөгөө оруулах</button>
        )}
      </div>
      {msg && <div style={noticeBox}>{msg}</div>}
      {loading ? <p>Ачааллаж байна…</p> : accounts.length === 0 ? (
        <div style={emptyBox}>
          Данс бүртгэгдээгүй байна. СӨХ-д тохирсон стандарт дансны төлөвлөгөөг дээрх товчоор оруулна уу.
        </div>
      ) : (
        <table style={table}>
          <thead><tr>
            <th style={th}>Код</th><th style={th}>Нэр</th><th style={th}>Төрөл</th>
            <th style={th}>Үлдэгдлийн тал</th><th style={th}>Гүйлгээ</th>
          </tr></thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} style={{ opacity: a.is_active ? 1 : 0.5 }}>
                <td style={{ ...td, fontWeight: a.is_leaf ? 400 : 700, paddingLeft: 12 + (a.code.length - 4) * 0 }}>{a.code}</td>
                <td style={{ ...td, fontWeight: a.is_leaf ? 400 : 600 }}>{a.name}</td>
                <td style={td}>{TYPE_MN[a.account_type] || a.account_type}</td>
                <td style={td}>{a.normal_balance === 'debit' ? 'Дебет' : 'Кредит'}</td>
                <td style={td}>{a.is_leaf ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Журнал ───
function JournalsTab() {
  const [journals, setJournals] = useState<Journal[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const [j, a] = await Promise.all([
      api.get('/admin/accounting/journals'),
      api.get('/admin/accounting/accounts'),
    ]);
    setJournals(j.items || []);
    setAccounts((a.items || []).filter((x: Account) => x.is_leaf && x.is_active));
  };
  useEffect(() => { load(); }, []);

  const post = async (id: string) => {
    setMsg('');
    try { await api.post(`/admin/accounting/journals/${id}/post`); await load(); }
    catch (e: any) { setMsg('Батлах алдаа: ' + (e?.message || e)); }
  };
  const del = async (id: string) => {
    if (!window.confirm('Ноорог бичилтийг устгах уу?')) return;
    try { await api.delete(`/admin/accounting/journals/${id}`); await load(); }
    catch (e: any) { setMsg('Устгах алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Журналын бичилт (гүйлгээ)</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...primaryBtn, background: '#fff', color: '#236844', border: '1px solid #236844' }} onClick={() => setShowImport(true)} disabled={accounts.length === 0}>
            ⇩ Зардлаас татах
          </button>
          <button style={primaryBtn} onClick={() => setShowForm(true)} disabled={accounts.length === 0}>
            + Шинэ гүйлгээ
          </button>
        </div>
      </div>
      {accounts.length === 0 && <div style={noticeBox}>Эхлээд "Дансны төлөвлөгөө" таб дээр данс оруулна уу.</div>}
      {msg && <div style={{ ...noticeBox, background: '#fef2f2', color: '#b91c1c' }}>{msg}</div>}

      {showForm && (
        <JournalForm accounts={accounts} onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await load(); }} />
      )}
      {showImport && (
        <ExpenseImportModal onClose={() => setShowImport(false)}
          onDone={async (m: string) => { setShowImport(false); setMsg(m); await load(); }} />
      )}

      <table style={table}>
        <thead><tr>
          <th style={th}>Огноо</th><th style={th}>Дугаар</th><th style={th}>Төрөл</th>
          <th style={th}>Утга</th><th style={{ ...th, textAlign: 'right' }}>Дүн</th>
          <th style={th}>Төлөв</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {journals.map(j => (
            <tr key={j.id}>
              <td style={td}>{j.doc_date}</td>
              <td style={td}>{j.doc_no}</td>
              <td style={td}>{JTYPE_MN[j.journal_type]}</td>
              <td style={td}>{j.description}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(j.total_debit)}</td>
              <td style={td}>
                <span style={{
                  ...badge,
                  background: j.status === 'posted' ? '#dcfce7' : j.status === 'draft' ? '#fef9c3' : '#e5e7eb',
                  color: j.status === 'posted' ? '#166534' : j.status === 'draft' ? '#854d0e' : '#374151',
                }}>{STATUS_MN[j.status]}</span>
              </td>
              <td style={td}>
                {j.status === 'draft' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={smallBtn} onClick={() => post(j.id)}>Батлах</button>
                    <button style={{ ...smallBtn, color: '#b91c1c', borderColor: '#fca5a5' }} onClick={() => del(j.id)}>Устгах</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {journals.length === 0 && <tr><td style={td} colSpan={7}>Гүйлгээ бүртгэгдээгүй байна.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** «Зардал бүртгэх» хуудасны татагдаагүй зардлуудыг сар сонгож журналд бичнэ.
 *  Төрөл → зардлын данс тааруулалтыг систем санана (expense_account_map). */
function ExpenseImportModal({ onClose, onDone }: { onClose: () => void; onDone: (msg: string) => void }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<any>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [bank, setBank] = useState(''); const [cash, setCash] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const fmtDate = (iso?: string | null) => iso ? iso.slice(0, 10).replace(/-/g, '.') : '';
  const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '');

  useEffect(() => {
    setData(null); setErr('');
    api.get(`/admin/accounting/expenses/unposted?year=${year}&month=${month}`).then((d: any) => {
      setData(d);
      const mp: Record<string, string> = {};
      (d.categories || []).forEach((c: any) => { if (c.account_id) mp[c.key] = c.account_id; });
      setMapping(mp); setBank(d.bank_account_id || ''); setCash(d.cash_account_id || '');
    }).catch((e: any) => setErr('Ачаалахад алдаа: ' + (e?.message || e)));
  }, [year, month]);

  const run = async () => {
    setBusy(true); setErr('');
    try {
      const r: any = await api.post('/admin/accounting/expenses/import', { year, month, mapping, bank_account_id: bank || null, cash_account_id: cash || null });
      onDone(`${r.message} — ${fmt(r.amount_total)}₮`);
    } catch (e: any) { let d = e?.message || String(e); try { d = JSON.parse(d).detail || d; } catch {} setErr(d); }
    finally { setBusy(false); }
  };
  const ready = data && data.total > 0 && (data.categories || []).every((c: any) => mapping[c.key]) && (!data.needs_bank || bank) && (!data.needs_cash || cash);

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBox, width: 820 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Зардлаас татах</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...input, marginTop: 0, width: 100 }}>{[0, 1, 2].map(i => now.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}</select>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...input, marginTop: 0, width: 110 }}>{Array.from({ length: 12 }, (_, i) => i + 1).map(mm => <option key={mm} value={mm}>{mm}-р сар</option>)}</select>
          </div>
        </div>
        <div style={{ ...noticeBox, background: '#f8fafc', color: '#475569' }}>Захирлын «Зардал бүртгэх» хуудсанд бүртгэсэн, журналд ороогүй зардлууд. Төрөл бүрийг зардлын данстай тааруулна — систем дараагийн удаад санана. Зардал бүр тусдаа бичилт (Дт зардал / Кт харилцах эсвэл касс) болж шууд батлагдана.</div>
        {err && <div style={{ ...noticeBox, background: '#fef2f2', color: '#b91c1c' }}>{err}</div>}
        {!data ? <div style={emptyBox}>Ачаалж байна…</div> : data.total === 0 ? <div style={emptyBox}>{year} оны {month}-р сард татагдаагүй зардал алга.</div> : (
          <>
            <table style={table}>
              <thead><tr><th style={th}>Зардлын төрөл</th><th style={{ ...th, textAlign: 'right' }}>Дүн</th><th style={th}>Зардлын данс (Дт)</th></tr></thead>
              <tbody>
                {data.categories.map((c: any) => (
                  <tr key={c.key}>
                    <td style={td}>{c.label}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmt(c.amount)}</td>
                    <td style={td}>
                      <select value={mapping[c.key] || ''} onChange={e => setMapping({ ...mapping, [c.key]: e.target.value })} style={{ ...input, marginTop: 0 }}>
                        <option value="">— данс сонгох —</option>
                        {data.expense_accounts.map((a: any) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {data.needs_bank && <tr><td style={td} colSpan={2}><b>Данснаас төлсөн</b> → кредит данс</td><td style={td}>
                  <select value={bank} onChange={e => setBank(e.target.value)} style={{ ...input, marginTop: 0 }}><option value="">— харилцах данс —</option>{data.asset_accounts.map((a: any) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}</select></td></tr>}
                {data.needs_cash && <tr><td style={td} colSpan={2}><b>Бэлнээр төлсөн</b> → кредит данс</td><td style={td}>
                  <select value={cash} onChange={e => setCash(e.target.value)} style={{ ...input, marginTop: 0 }}><option value="">— касс —</option>{data.asset_accounts.map((a: any) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}</select></td></tr>}
              </tbody>
            </table>
            <h4 style={{ margin: '16px 0 8px' }}>Зардлууд ({data.total}) — нийт {fmt(data.amount_total)}</h4>
            <table style={table}>
              <thead><tr><th style={th}>Огноо</th><th style={th}>Төрөл</th><th style={th}>Хэнд</th><th style={th}>Хэлбэр</th><th style={{ ...th, textAlign: 'right' }}>Дүн</th><th style={th}>Баримт</th></tr></thead>
              <tbody>
                {data.items.map((x: any) => (
                  <tr key={x.id}>
                    <td style={td}>{fmtDate(x.expense_date)}</td><td style={td}>{x.category_label}{x.work_title ? <div style={{ fontSize: 12, color: '#64748b' }}>🔧 {x.work_title}</div> : null}</td>
                    <td style={td}>{x.payee}{x.description ? <div style={{ fontSize: 12, color: '#64748b' }}>{x.description}</div> : null}</td>
                    <td style={td}>{x.payment_method === 'cash' ? 'Бэлэн' : 'Данс'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmt(x.amount)}</td>
                    <td style={td}>{x.images.map((im: any) => <a key={im.id} href={`${API_ORIGIN}${im.url}`} target="_blank" rel="noreferrer"><img src={`${API_ORIGIN}${im.url}`} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, marginRight: 4, border: '1px solid #e2e8f0' }} /></a>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button style={smallBtn} onClick={onClose}>Хаах</button>
          <button style={{ ...primaryBtn, opacity: ready && !busy ? 1 : .5 }} disabled={!ready || busy} onClick={run}>{busy ? 'Бичиж байна…' : `Журналд бичих (${data?.total || 0})`}</button>
        </div>
      </div>
    </div>
  );
}

function JournalForm({ accounts, onClose, onSaved }: { accounts: Account[]; onClose: () => void; onSaved: () => void; }) {
  const [docDate, setDocDate] = useState('');
  const [jtype, setJtype] = useState('cash');
  const [desc, setDesc] = useState('');
  const [lines, setLines] = useState<JournalLine[]>([
    { account_id: '', debit: 0, credit: 0 }, { account_id: '', debit: 0, credit: 0 },
  ]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [err, setErr] = useState('');
  useEffect(() => { api.get('/admin/accounting/partners').then(r => setPartners(r.items || [])).catch(() => {}); }, []);

  const totD = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totC = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = totD === totC && totD > 0;

  const setLine = (i: number, patch: Partial<JournalLine>) =>
    setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const save = async () => {
    setErr('');
    if (!docDate) { setErr('Огноо оруулна уу'); return; }
    const payload = {
      journal_type: jtype, doc_date: docDate, description: desc,
      lines: lines.filter(l => l.account_id).map(l => ({
        account_id: l.account_id, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0,
        description: l.description || '', partner_id: l.partner_id || null,
      })),
    };
    try { await api.post('/admin/accounting/journals', payload); onSaved(); }
    catch (e: any) { setErr(e?.message || String(e)); }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Шинэ гүйлгээ</h3>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <label style={{ flex: 1 }}>Огноо<input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} style={input} /></label>
          <label style={{ flex: 1 }}>Төрөл
            <select value={jtype} onChange={e => setJtype(e.target.value)} style={input}>
              {Object.entries(JTYPE_MN).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>
        <label>Утга<input value={desc} onChange={e => setDesc(e.target.value)} style={input} placeholder="Гүйлгээний тайлбар" /></label>

        <table style={{ ...table, marginTop: 12 }}>
          <thead><tr><th style={th}>Данс</th><th style={{ ...th, textAlign: 'right' }}>Дебет</th><th style={{ ...th, textAlign: 'right' }}>Кредит</th><th style={th}></th></tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td style={td}>
                  <select value={l.account_id} onChange={e => setLine(i, { account_id: e.target.value })} style={{ ...input, marginTop: 0 }}>
                    <option value="">— данс сонгох —</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                  </select>
                </td>
                <td style={td}><input type="number" value={l.debit || ''} onChange={e => setLine(i, { debit: Number(e.target.value), credit: 0 })} style={{ ...input, marginTop: 0, textAlign: 'right' }} /></td>
                <td style={td}><input type="number" value={l.credit || ''} onChange={e => setLine(i, { credit: Number(e.target.value), debit: 0 })} style={{ ...input, marginTop: 0, textAlign: 'right' }} /></td>
                <td style={td}>{lines.length > 2 && <button style={smallBtn} onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>×</button>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...td, fontWeight: 700 }}>Нийт</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(totD)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(totC)}</td>
              <td style={td}></td>
            </tr>
          </tfoot>
        </table>
        <button style={smallBtn} onClick={() => setLines([...lines, { account_id: '', debit: 0, credit: 0 }])}>+ Мөр нэмэх</button>

        <div style={{ marginTop: 12, color: balanced ? '#166534' : '#b91c1c', fontWeight: 600 }}>
          {balanced ? '✓ Дебет ба кредит тэнцсэн' : `Зөрүү: ${fmt(Math.abs(totD - totC))} (дебет=кредит байх ёстой)`}
        </div>
        {err && <div style={{ ...noticeBox, background: '#fef2f2', color: '#b91c1c' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button style={smallBtn} onClick={onClose}>Болих</button>
          <button style={primaryBtn} onClick={save} disabled={!balanced}>Хадгалах</button>
        </div>
      </div>
    </div>
  );
}

// ─── Гүйлгээ баланс ───
function TrialBalanceTab() {
  const [rows, setRows] = useState<TrialRow[]>([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0, balanced: true });
  const [loading, setLoading] = useState(true);
  const [ledgerAcc, setLedgerAcc] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/admin/accounting/trial-balance');
        setRows(r.items || []);
        setTotals({ debit: r.total_debit, credit: r.total_credit, balanced: r.balanced });
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Гүйлгээ баланс (батлагдсан бичилт)</h3>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Дансны код дээр дарж дэлгэрэнгүй хуулгыг харна.</div>
      {ledgerAcc && <AccountLedgerModal accountId={ledgerAcc} onClose={() => setLedgerAcc(null)} />}
      {loading ? <p>Ачааллаж байна…</p> : (
        <table style={table}>
          <thead><tr>
            <th style={th}>Код</th><th style={th}>Дансны нэр</th>
            <th style={{ ...th, textAlign: 'right' }}>Дебет</th>
            <th style={{ ...th, textAlign: 'right' }}>Кредит</th>
            <th style={{ ...th, textAlign: 'right' }}>Үлдэгдэл</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.account_id}>
                <td style={{ ...td, color: '#1c5238', fontWeight: 600, cursor: 'pointer' }} onClick={() => setLedgerAcc(r.account_id)}>{r.code}</td><td style={td}>{r.name}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(r.debit)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(r.credit)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(r.balance)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={td} colSpan={5}>Батлагдсан гүйлгээ алга.</td></tr>}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={2}>Нийт</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(totals.debit)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(totals.credit)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: totals.balanced ? '#166534' : '#b91c1c' }}>
                {totals.balanced ? '✓ тэнцсэн' : 'тэнцээгүй'}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

// ─── Үндсэн хөрөнгө (элэгдэл) ───
interface FixedAsset {
  id: string; name: string; acquisition_cost: number; salvage_value: number;
  useful_life_months: number; start_date: string; accumulated_depreciation: number;
  book_value: number; monthly_depreciation: number; status: string;
}
const FA_STATUS_MN: Record<string, string> = {
  active: 'Элэгдэж буй', fully_depreciated: 'Бүрэн элэгдсэн', disposed: 'Данснаас хассан',
};

interface AvailAsset {
  asset_id: string; name: string; category: string; status: string;
  purchase_cost: number; current_value: number; purchase_date: string | null;
  serial_number: string; deprec_id: string | null;
}
const ASSET_CAT_MN: Record<string, string> = {
  building: 'Барилга', elevator: 'Лифт', parking: 'Зогсоол', playground: 'Тоглоомын талбай',
  security: 'Хамгаалалт', utility: 'Инженерийн хэрэгсэл', furniture: 'Тавилга', other: 'Бусад',
};

function FixedAssetsTab() {
  const [items, setItems] = useState<FixedAsset[]>([]);
  const [avail, setAvail] = useState<AvailAsset[]>([]);
  const [chartReady, setChartReady] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [prefill, setPrefill] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const load = async () => {
    const [fa, acc, av] = await Promise.all([
      api.get('/admin/accounting/fixed-assets'),
      api.get('/admin/accounting/accounts'),
      api.get('/admin/accounting/fixed-assets/available-assets'),
    ]);
    setItems(fa.items || []);
    setAvail(av.items || []);
    setChartReady((acc.items || []).some((a: Account) => a.code === '1520'));
  };
  useEffect(() => { load(); }, []);

  const openForm = (a?: AvailAsset) => {
    setPrefill(a ? {
      name: a.name, cost: a.current_value || a.purchase_cost || 0,
      start: a.purchase_date || '', asset_id: a.asset_id,
    } : null);
    setShowForm(true);
  };

  const runAll = async () => {
    setMsg('');
    try {
      const r = await api.post(`/admin/accounting/fixed-assets/run-all?year=${year}&month=${month}`);
      const posted = (r.results || []).filter((x: any) => x.status === 'posted').length;
      setMsg(`${year}-${String(month).padStart(2, '0')}: ${posted} хөрөнгийн элэгдэл бодогдож нийт ${fmt(r.posted_total)}₮ журналд бичигдлээ.`);
      await load();
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };
  const dispose = async (id: string) => {
    const ans = window.prompt('ҮХ акт — данснаас хасах. Борлуулсан дүн (₮)-г оруулна уу. Зүгээр акталж хасах бол 0.', '0');
    if (ans === null) return;
    const proceeds = Number(ans) || 0;
    try {
      const r = await api.post(`/admin/accounting/fixed-assets/${id}/dispose`, {
        proceeds, proceeds_account_code: '1110', doc_date: `${year}-${String(month).padStart(2, '0')}-28`,
      });
      setMsg(`Хасагдлаа. Дансны үнэ ${fmt(r.book_value)}₮, ${r.gain_loss >= 0 ? 'ашиг' : 'алдагдал'} ${fmt(Math.abs(r.gain_loss))}₮.`);
      await load();
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Үндсэн хөрөнгө — элэгдэл</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...input, marginTop: 0, width: 'auto' }}>
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...input, marginTop: 0, width: 'auto' }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}-р сар</option>)}
          </select>
          <button style={smallBtn} onClick={runAll} disabled={items.length === 0}>Сарын элэгдэл бодох</button>
          <button style={primaryBtn} onClick={() => openForm()} disabled={!chartReady}>+ Гараар бүртгэх</button>
        </div>
      </div>
      {!chartReady && <div style={noticeBox}>Эхлээд "Дансны төлөвлөгөө" таб дээр стандарт төлөвлөгөө оруулна уу (1520/6300 данс шаардлагатай).</div>}
      {msg && <div style={noticeBox}>{msg}</div>}

      {/* Хөрөнгө (assets) хуудсанд бүртгэгдсэн хөрөнгүүд */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Хөрөнгө хуудсанд бүртгэгдсэн хөрөнгүүд</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>«Бүртгэл → Хөрөнгө» хуудсанд оруулсан хөрөнгүүд. «Элэгдэл тохируулах» дарж, өртөг/огноог автоматаар татан элэгдэлд бүртгэнэ.</div>
        <table style={table}>
          <thead><tr>
            <th style={th}>Нэр</th><th style={th}>Ангилал</th>
            <th style={{ ...th, textAlign: 'right' }}>Өртөг</th>
            <th style={{ ...th, textAlign: 'right' }}>Дансны үнэ</th>
            <th style={th}>Элэгдэл</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {avail.map(a => (
              <tr key={a.asset_id}>
                <td style={td}>{a.name}{a.serial_number ? <span style={{ color: '#94a3b8', fontSize: 12 }}> · {a.serial_number}</span> : null}</td>
                <td style={td}>{ASSET_CAT_MN[a.category] || a.category}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(a.purchase_cost)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(a.current_value)}</td>
                <td style={td}>
                  {a.deprec_id
                    ? <span style={{ ...badge, background: '#dcfce7', color: '#166534' }}>✓ Элэгдэлд орсон</span>
                    : <span style={{ ...badge, background: '#f1f5f9', color: '#64748b' }}>Ороогүй</span>}
                </td>
                <td style={td}>{!a.deprec_id && <button style={smallBtn} onClick={() => openForm(a)} disabled={!chartReady}>Элэгдэл тохируулах</button>}</td>
              </tr>
            ))}
            {avail.length === 0 && <tr><td style={td} colSpan={6}>«Хөрөнгө» хуудсанд хөрөнгө бүртгэгдээгүй байна.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && <FixedAssetForm prefill={prefill} onClose={() => setShowForm(false)} onSaved={async () => { setShowForm(false); await load(); }} />}

      <table style={table}>
        <thead><tr>
          <th style={th}>Нэр</th>
          <th style={{ ...th, textAlign: 'right' }}>Өртөг</th>
          <th style={{ ...th, textAlign: 'right' }}>Сарын элэгдэл</th>
          <th style={{ ...th, textAlign: 'right' }}>Хуримт. элэгдэл</th>
          <th style={{ ...th, textAlign: 'right' }}>Дансны үнэ</th>
          <th style={th}>Ашиглалт</th>
          <th style={th}>Төлөв</th>
          <th style={th}></th>
        </tr></thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id}>
              <td style={td}>{a.name}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(a.acquisition_cost)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(a.monthly_depreciation)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(a.accumulated_depreciation)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(a.book_value)}</td>
              <td style={td}>{a.useful_life_months} сар</td>
              <td style={td}>
                <span style={{
                  ...badge,
                  background: a.status === 'active' ? '#dbeafe' : a.status === 'fully_depreciated' ? '#dcfce7' : '#e5e7eb',
                  color: a.status === 'active' ? '#1e40af' : a.status === 'fully_depreciated' ? '#166534' : '#374151',
                }}>{FA_STATUS_MN[a.status]}</span>
              </td>
              <td style={td}>{a.status === 'active' && <button style={{ ...smallBtn, color: '#b91c1c', borderColor: '#fca5a5' }} onClick={() => dispose(a.id)}>Данснаас хасах</button>}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td style={td} colSpan={8}>Хөрөнгө бүртгэгдээгүй байна.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FixedAssetForm({ onClose, onSaved, prefill }: { onClose: () => void; onSaved: () => void; prefill?: any; }) {
  const [name, setName] = useState(prefill?.name || '');
  const [cost, setCost] = useState<number>(prefill?.cost || 0);
  const [salvage, setSalvage] = useState<number>(0);
  const [life, setLife] = useState<number>(60);
  const [start, setStart] = useState(prefill?.start || '');
  const [book, setBook] = useState(false);
  const [payAcc, setPayAcc] = useState('1120');
  const [err, setErr] = useState('');
  const monthly = life > 0 ? (cost - salvage) / life : 0;
  const linked = !!prefill?.asset_id;

  const save = async () => {
    setErr('');
    if (!name.trim()) { setErr('Нэр оруулна уу'); return; }
    if (!start) { setErr('Эхлэх огноо оруулна уу'); return; }
    try {
      await api.post('/admin/accounting/fixed-assets', {
        name, acquisition_cost: cost, salvage_value: salvage, useful_life_months: life,
        start_date: start, asset_id: prefill?.asset_id || null,
        book_acquisition: book, payment_account_code: payAcc,
      });
      onSaved();
    } catch (e: any) { setErr(e?.message || String(e)); }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBox, width: 480 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{linked ? 'Элэгдэл тохируулах' : 'Үндсэн хөрөнгө бүртгэх'}</h3>
        {linked && <div style={{ ...noticeBox, marginBottom: 12 }}>«Хөрөнгө» хуудсаас татсан — өртөг/огноог шалгаад ашиглалтын хугацааг оруулна уу.</div>}
        <label>Хөрөнгийн нэр<input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="Жишээ: Лифт, Камер, Гэрэлтүүлэг" readOnly={linked} /></label>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ flex: 1 }}>Анхны өртөг (₮)<input type="number" value={cost || ''} onChange={e => setCost(Number(e.target.value))} style={input} /></label>
          <label style={{ flex: 1 }}>Үлдэгдэл өртөг (₮)<input type="number" value={salvage || ''} onChange={e => setSalvage(Number(e.target.value))} style={input} /></label>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ flex: 1 }}>Ашиглалт (сар)<input type="number" value={life || ''} onChange={e => setLife(Number(e.target.value))} style={input} /></label>
          <label style={{ flex: 1 }}>Эхлэх огноо<input type="date" value={start} onChange={e => setStart(e.target.value)} style={input} /></label>
        </div>
        <div style={{ marginTop: 10, color: '#236844', fontWeight: 600 }}>
          Сарын элэгдэл: {fmt(monthly)}₮ (шулуун шугам)
        </div>
        {/* ҮХ орлого — худалдан авалтыг журналд бичих */}
        <div style={{ marginTop: 12, padding: 10, background: 'var(--surface-2, #f8faf6)', borderRadius: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={book} onChange={e => setBook(e.target.checked)} />
            <span style={{ fontSize: 13 }}>Худалдан авалтыг журналд бичих (Дт ҮХ / Кт төлбөр)</span>
          </label>
          {book && (
            <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>Төлбөрийн данс
              <select value={payAcc} onChange={e => setPayAcc(e.target.value)} style={{ ...input, marginTop: 4 }}>
                <option value="1110">1110 · Касс</option>
                <option value="1120">1120 · Харилцах данс</option>
                <option value="3100">3100 · Дансны өглөг (зээлээр)</option>
              </select>
            </label>
          )}
        </div>
        {err && <div style={{ ...noticeBox, background: '#fef2f2', color: '#b91c1c' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button style={smallBtn} onClick={onClose}>Болих</button>
          <button style={primaryBtn} onClick={save}>Хадгалах</button>
        </div>
      </div>
    </div>
  );
}

// ─── Цалин (журналд бүртгэх) ───
interface PayrollRow {
  payroll_id: string; gross: number; base_salary: number; bonus: number;
  deduction: number; net_amount: number; status: string; accrued: boolean; payment_posted: boolean;
}

function PayrollTab() {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [chartReady, setChartReady] = useState(true);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, acc] = await Promise.all([
        api.get(`/admin/accounting/payroll?period=${period}`),
        api.get('/admin/accounting/accounts'),
      ]);
      setRows(pr.items || []);
      setChartReady((acc.items || []).some((a: Account) => a.code === '3300'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const accrueAll = async () => {
    setMsg('');
    try {
      const r = await api.post(`/admin/accounting/payroll/post-accrual?period=${period}`);
      setMsg(`Тооцоо: ${r.posted} цалин журналд бичигдлээ (нийт ${fmt(r.total_gross)}₮), ${r.skipped} алгассан.`);
      await load();
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };
  const payOne = async (id: string) => {
    setMsg('');
    try { await api.post(`/admin/accounting/payroll/${id}/post-payment`); await load(); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Цалин — журналд бүртгэх</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto' }} />
          <button style={primaryBtn} onClick={accrueAll} disabled={!chartReady || rows.length === 0}>Сарын тооцоо бүртгэх</button>
        </div>
      </div>
      {!chartReady && <div style={noticeBox}>Эхлээд "Дансны төлөвлөгөө" таб дээр стандарт төлөвлөгөө оруулна уу (6100/3300 данс).</div>}
      {msg && <div style={noticeBox}>{msg}</div>}

      {loading ? <p>Ачааллаж байна…</p> : (
        <table style={table}>
          <thead><tr>
            <th style={th}>Ажилтан</th>
            <th style={{ ...th, textAlign: 'right' }}>Үндсэн</th>
            <th style={{ ...th, textAlign: 'right' }}>Урамшуулал</th>
            <th style={{ ...th, textAlign: 'right' }}>Суутгал</th>
            <th style={{ ...th, textAlign: 'right' }}>Цэвэр дүн</th>
            <th style={th}>Журнал</th>
            <th style={th}></th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.payroll_id}>
                <td style={td}>{r.payroll_id.slice(0, 8)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(r.base_salary)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(r.bonus)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(r.deduction)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(r.net_amount)}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span style={{ ...badge, background: r.accrued ? '#dcfce7' : '#f1f5f9', color: r.accrued ? '#166534' : '#94a3b8' }}>Тооцоо</span>
                    <span style={{ ...badge, background: r.payment_posted ? '#dcfce7' : '#f1f5f9', color: r.payment_posted ? '#166534' : '#94a3b8' }}>Олголт</span>
                  </div>
                </td>
                <td style={td}>
                  {r.accrued && !r.payment_posted && <button style={smallBtn} onClick={() => payOne(r.payroll_id)}>Олголт бүртгэх</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={td} colSpan={7}>Энэ сард цалингийн тооцоо алга (Ажилчид/Цалин цэсэнд үүсгэнэ).</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Санхүүгийн тайлан ───
type ReportKind = 'income' | 'balance' | 'cash' | 'equity' | 'journal' | 'salary' | 'nd7' | 'nd8' | 'tt11' | 'aanoat';
const REPORT_GROUPS: { group: string; items: { k: ReportKind; label: string }[] }[] = [
  { group: 'Санхүүгийн тайлан', items: [
    { k: 'income', label: 'ОДТ — Орлогын дэлгэрэнгүй тайлан' },
    { k: 'balance', label: 'ББТ — Санхүүгийн байдлын тайлан' },
    { k: 'cash', label: 'МГТ — Мөнгөн гүйлгээний тайлан' },
    { k: 'equity', label: 'ӨӨТ — Өмчийн өөрчлөлтийн тайлан' },
  ] },
  { group: 'Журнал', items: [
    { k: 'journal', label: 'Ерөнхий журнал (бүх бичилт)' },
  ] },
  { group: 'Татвар / Цалингийн маягт', items: [
    { k: 'salary', label: 'Цалингийн хүснэгт' },
    { k: 'nd7', label: 'НД7 — Нийгмийн даатгал (ажилтнаар)' },
    { k: 'nd8', label: 'НД8 — Нийгмийн даатгал (нэгдсэн)' },
    { k: 'tt11', label: 'ТТ11 — ХХОАТ суутгал' },
    { k: 'aanoat', label: 'ААНОАТ — Орлогын албан татвар' },
  ] },
];

const PERIOD_KINDS: ReportKind[] = ['salary', 'nd7', 'nd8', 'tt11'];

function ReportsTab() {
  const now = new Date();
  const y = now.getFullYear();
  const [kind, setKind] = useState<ReportKind>('income');
  const [from, setFrom] = useState(`${y}-01-01`);
  const [to, setTo] = useState(`${y}-12-31`);
  const [asOf, setAsOf] = useState(`${y}-12-31`);
  const [period, setPeriod] = useState(`${y}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [year, setYear] = useState(y);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const run = async () => {
    setLoading(true); setErr(''); setData(null);
    try {
      let url = '';
      if (kind === 'balance') url = `/admin/accounting/reports/balance-sheet?as_of=${asOf}`;
      else if (kind === 'income') url = `/admin/accounting/reports/income-statement?date_from=${from}&date_to=${to}`;
      else if (kind === 'cash') url = `/admin/accounting/reports/cash-flow?date_from=${from}&date_to=${to}`;
      else if (kind === 'journal') url = `/admin/accounting/reports/general-journal?date_from=${from}&date_to=${to}`;
      else if (kind === 'salary') url = `/admin/accounting/payroll-reports/salary-sheet?period=${period}`;
      else if (kind === 'nd7') url = `/admin/accounting/payroll-reports/nd7?period=${period}`;
      else if (kind === 'nd8') url = `/admin/accounting/payroll-reports/nd8?period=${period}`;
      else if (kind === 'tt11') url = `/admin/accounting/payroll-reports/tt11?period=${period}`;
      else if (kind === 'aanoat') url = `/admin/accounting/payroll-reports/aanoat?year=${year}`;
      else url = `/admin/accounting/reports/equity-changes?date_from=${from}&date_to=${to}`;
      setData(await api.get(url));
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [kind]);

  const Section = ({ title, rows, total, totalLabel }: { title: string; rows: any[]; total: number; totalLabel: string }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#236844' }}>{title}</div>
      <table style={table}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...td, width: 90 }}>{r.code}</td>
              <td style={td}>{r.name}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(r.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={td} colSpan={3}>—</td></tr>}
          <tr>
            <td style={{ ...td, fontWeight: 700 }} colSpan={2}>{totalLabel}</td>
            <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={kind} onChange={e => { setData(null); setKind(e.target.value as ReportKind); }} style={{ ...input, marginTop: 0, width: 'auto' }}>
          {REPORT_GROUPS.map(g => (
            <optgroup key={g.group} label={g.group}>
              {g.items.map(it => <option key={it.k} value={it.k}>{it.label}</option>)}
            </optgroup>
          ))}
        </select>
        {kind === 'balance' ? (
          <label style={{ fontSize: 13 }}>Тайлагнах өдөр <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }} /></label>
        ) : PERIOD_KINDS.includes(kind) ? (
          <label style={{ fontSize: 13 }}>Тайлант сар <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }} /></label>
        ) : kind === 'aanoat' ? (
          <label style={{ fontSize: 13 }}>Тайлант жил
            <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block', marginLeft: 6 }}>
              {[y - 1, y, y + 1].map(yy => <option key={yy} value={yy}>{yy}</option>)}
            </select>
          </label>
        ) : (
          <>
            <label style={{ fontSize: 13 }}>Эхлэх <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }} /></label>
            <label style={{ fontSize: 13 }}>Дуусах <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }} /></label>
          </>
        )}
        <button style={primaryBtn} onClick={run}>Тайлан гаргах</button>
      </div>

      {err && <div style={{ ...noticeBox, background: '#fef2f2', color: '#b91c1c' }}>{err}</div>}
      {loading ? <p>Тооцоолж байна…</p> : !data ? null : (
        <div>
          {kind === 'income' && data.income && (
            <>
              <Section title="Орлого" rows={data.income} total={data.total_income} totalLabel="Нийт орлого" />
              <Section title="Зардал" rows={data.expense} total={data.total_expense} totalLabel="Нийт зардал" />
              <div style={{ ...resultBox, background: data.net_profit >= 0 ? '#ecfdf5' : '#fef2f2', color: data.net_profit >= 0 ? '#065f46' : '#b91c1c' }}>
                Тайлант үеийн цэвэр {data.net_profit >= 0 ? 'ашиг' : 'алдагдал'}: {fmt(data.net_profit)}₮
              </div>
            </>
          )}
          {kind === 'balance' && data.assets && (
            <>
              <Section title="ХӨРӨНГӨ" rows={data.assets} total={data.total_assets} totalLabel="Нийт хөрөнгө" />
              <Section title="ӨР ТӨЛБӨР" rows={data.liabilities} total={data.total_liabilities} totalLabel="Нийт өр төлбөр" />
              <Section title="ЭЗДИЙН ӨМЧ" rows={data.equity} total={data.total_equity} totalLabel="Нийт өмч" />
              <div style={{ ...resultBox, background: data.balanced ? '#ecfdf5' : '#fef2f2', color: data.balanced ? '#065f46' : '#b91c1c' }}>
                {data.balanced ? '✓ Баланс тэнцсэн' : '⚠ Баланс тэнцээгүй'}: Хөрөнгө {fmt(data.total_assets)} = Өр+Өмч {fmt(data.total_liabilities_equity)}
              </div>
            </>
          )}
          {kind === 'cash' && data.accounts && (
            <table style={table}>
              <tbody>
                <tr><td style={td}>Эхний үлдэгдэл</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.opening_balance)}</td></tr>
                <tr><td style={td}>Мөнгө орсон (+)</td><td style={{ ...td, textAlign: 'right', color: '#166534' }}>{fmt(data.total_inflow)}</td></tr>
                <tr><td style={td}>Мөнгө гарсан (−)</td><td style={{ ...td, textAlign: 'right', color: '#b91c1c' }}>{fmt(data.total_outflow)}</td></tr>
                <tr><td style={{ ...td, fontWeight: 700 }}>Эцсийн үлдэгдэл</td><td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.closing_balance)}</td></tr>
              </tbody>
            </table>
          )}
          {kind === 'equity' && 'closing_equity' in data && (
            <table style={table}>
              <tbody>
                <tr><td style={td}>Эхний өмч</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.opening_equity)}</td></tr>
                <tr><td style={td}>Тайлант үеийн үр дүн</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.period_result)}</td></tr>
                <tr><td style={td}>Бусад өөрчлөлт</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.other_changes)}</td></tr>
                <tr><td style={{ ...td, fontWeight: 700 }}>Эцсийн өмч</td><td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.closing_equity)}</td></tr>
              </tbody>
            </table>
          )}
          {kind === 'journal' && data.items && (
            <div>
              <div style={{ marginBottom: 8, color: '#64748b', fontSize: 13 }}>{data.count} баримт, {data.items.length} мөр</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={table}>
                  <thead><tr>
                    <th style={th}>Огноо</th><th style={th}>Дугаар</th><th style={th}>Данс</th><th style={th}>Утга</th>
                    <th style={{ ...th, textAlign: 'right' }}>Дебет</th><th style={{ ...th, textAlign: 'right' }}>Кредит</th>
                  </tr></thead>
                  <tbody>
                    {data.items.map((l: any, i: number) => (
                      <tr key={i}>
                        <td style={td}>{l.doc_date}</td><td style={td}>{l.doc_no}</td>
                        <td style={td}>{l.account_code} · {l.account_name}</td><td style={td}>{l.description}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{l.debit ? fmt(l.debit) : ''}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{l.credit ? fmt(l.credit) : ''}</td>
                      </tr>
                    ))}
                    {data.items.length === 0 && <tr><td style={td} colSpan={6}>Гүйлгээ алга.</td></tr>}
                  </tbody>
                  <tfoot><tr>
                    <td style={{ ...td, fontWeight: 700 }} colSpan={4}>Нийт</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.total_debit)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.total_credit)}</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Цалингийн хүснэгт */}
          {kind === 'salary' && data.items && (
            <PayrollFormTable
              cols={['Ажилтан', 'РД', 'Албан тушаал', 'Цалин', 'НДШ', 'ХХОАТ', 'Гарт олгох']}
              rows={data.items.map((r: any) => [r.name, r.reg_no, r.position, r.gross, r.si_emp, r.pit, r.net])}
              nums={[3, 4, 5, 6]} totals={['Нийт', '', '', data.totals.gross, data.totals.si_emp, data.totals.pit, data.totals.net]}
              rates={data.rates} />
          )}
          {/* НД7 */}
          {kind === 'nd7' && data.items && (
            <PayrollFormTable
              cols={['Ажилтан', 'РД', 'Цалин', 'НДШ (ажилтан)', 'НДШ (ажил олгогч)', 'Нийт НДШ']}
              rows={data.items.map((r: any) => [r.name, r.reg_no, r.gross, r.si_emp, r.si_empr, r.si_total])}
              nums={[2, 3, 4, 5]} totals={['Нийт', '', data.totals.gross, data.totals.si_emp, data.totals.si_empr, data.totals.si_total]}
              rates={data.rates} formName="НД7 — Нийгмийн даатгалын шимтгэл" />
          )}
          {/* НД8 — нэгдсэн дүн */}
          {kind === 'nd8' && data.summary && (
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: '#236844' }}>НД8 — Нийгмийн даатгалын шимтгэлийн нэгдсэн тайлан ({data.period})</div>
              <table style={table}><tbody>
                <tr><td style={td}>Даатгуулагчийн тоо</td><td style={{ ...td, textAlign: 'right' }}>{data.summary.headcount}</td></tr>
                <tr><td style={td}>Нийт цалин (ХЖ сан)</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.summary.gross)}</td></tr>
                <tr><td style={td}>Ажилтнаас суутгасан НДШ ({(data.rates.si_employee * 100).toFixed(1)}%)</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.summary.si_emp)}</td></tr>
                <tr><td style={td}>Ажил олгогчийн НДШ ({(data.rates.si_employer * 100).toFixed(1)}%)</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.summary.si_empr)}</td></tr>
                <tr><td style={{ ...td, fontWeight: 700 }}>Нийт төлөх НДШ</td><td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#b45309' }}>{fmt(data.summary.si_total)}</td></tr>
              </tbody></table>
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>⚠️ Хувь хэмжээ тохируулж болно. Мөрдөж буй журмаар шалгана уу.</div>
            </div>
          )}
          {/* ТТ11 */}
          {kind === 'tt11' && data.items && (
            <PayrollFormTable
              cols={['Ажилтан', 'РД', 'Орлого', 'НДШ', 'Татвар ногдох', 'ХХОАТ (10%)']}
              rows={data.items.map((r: any) => [r.name, r.reg_no, r.income, r.si_emp, r.taxable, r.pit])}
              nums={[2, 3, 4, 5]} totals={['Нийт', '', data.totals.income, data.totals.si_emp, data.totals.taxable, data.totals.pit]}
              rates={data.rates} formName="ТТ11 — Цалингаас суутгасан ХХОАТ" />
          )}
          {/* ААНОАТ */}
          {kind === 'aanoat' && data.taxable_profit !== undefined && (
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>ААНОАТ — Аж ахуйн нэгжийн орлогын албан татвар ({data.year})</div>
              <table style={table}><tbody>
                <tr><td style={td}>Нийт орлого</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.total_income)}</td></tr>
                <tr><td style={td}>Нийт зардал</td><td style={{ ...td, textAlign: 'right' }}>{fmt(data.total_expense)}</td></tr>
                <tr><td style={{ ...td, fontWeight: 700 }}>Татвар ногдох ашиг</td><td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.taxable_profit)}</td></tr>
                <tr><td style={td}>Татварын хувь</td><td style={{ ...td, textAlign: 'right' }}>{(data.rate * 100).toFixed(0)}%</td></tr>
                <tr><td style={{ ...td, fontWeight: 700 }}>Ногдох татвар</td><td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#b45309' }}>{fmt(data.tax)}</td></tr>
              </tbody></table>
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>⚠️ {data.note}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Цалингийн маягтын хүснэгт (НД7/ТТ11/хүснэгт) — хувь хэмжээний тэмдэглэлтэй
function PayrollFormTable({ cols, rows, nums, totals, rates, formName }:
  { cols: string[]; rows: any[][]; nums: number[]; totals: any[]; rates: any; formName?: string }) {
  return (
    <div>
      {formName && <div style={{ fontWeight: 700, marginBottom: 8, color: '#236844' }}>{formName}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead><tr>{cols.map((c, i) => <th key={i} style={{ ...th, textAlign: nums.includes(i) ? 'right' : 'left' }}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>{r.map((cell, ci) => <td key={ci} style={{ ...td, textAlign: nums.includes(ci) ? 'right' : 'left' }}>{nums.includes(ci) ? fmt(cell) : cell}</td>)}</tr>
            ))}
            {rows.length === 0 && <tr><td style={td} colSpan={cols.length}>Тухайн сард цалин алга.</td></tr>}
          </tbody>
          <tfoot><tr>{totals.map((c, i) => <td key={i} style={{ ...td, fontWeight: 700, textAlign: nums.includes(i) ? 'right' : 'left' }}>{nums.includes(i) ? fmt(c) : c}</td>)}</tr></tfoot>
        </table>
      </div>
      <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>
        ⚠️ Хувь хэмжээ (тохируулж болно): НДШ ажилтан {(rates.si_employee * 100).toFixed(1)}%, ажил олгогч {(rates.si_employer * 100).toFixed(1)}%, ХХОАТ {(rates.pit * 100).toFixed(0)}%. Мөрдөж буй журмаар шалгана уу.
      </div>
    </div>
  );
}

// ─── E-Balance (Сангийн яам) ───
interface EbStatus {
  configured: boolean; can_submit: boolean; env: string; grant_type?: string;
  profile: { org_reg_no: string; user_reg_no: string; per_map_user_role_id: string; default_writing_config_code: string } | null;
  mapped_accounts: number;
}
interface MapRow { id: string; code: string; name: string; account_type: string; ebalance_map_code: string; }
interface Submission { id: string; writing_config_code: string; as_of: string; cell_count: number; status: string; message: string; submitted_at: string | null; }

const SUB_STATUS_MN: Record<string, string> = {
  pending: 'Бэлтгэсэн', saved: 'Хадгалсан', sent: 'Илгээсэн', accepted: 'Хүлээн авсан', rejected: 'Татгалзсан', failed: 'Алдаа',
};

function EBalanceTab() {
  const [st, setSt] = useState<EbStatus | null>(null);
  const [maps, setMaps] = useState<MapRow[]>([]);
  const [subs, setSubs] = useState<Submission[]>([]);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [configs, setConfigs] = useState<{ code: string; name: string }[]>([]);
  const [diag, setDiag] = useState<any>(null);
  const now = new Date();
  const [asOf, setAsOf] = useState(`${now.getFullYear()}-12-31`);
  const [code, setCode] = useState('');

  const loadConfigs = async () => {
    setMsg('');
    try {
      const r = await api.get('/admin/accounting/ebalance/writing-configs');
      setConfigs(r.items || []);
      setMsg(`E-Balance-аас ${r.total} тайлант үе татагдлаа.`);
    } catch (e: any) { setMsg('Тайлант үе татаж чадсангүй: ' + (e?.message || e)); }
  };

  const load = async () => {
    const [s, m, sub] = await Promise.all([
      api.get('/admin/accounting/ebalance/status'),
      api.get('/admin/accounting/ebalance/account-map'),
      api.get('/admin/accounting/ebalance/submissions'),
    ]);
    setSt(s); setMaps(m.items || []); setSubs(sub.items || []);
    if (s.profile?.default_writing_config_code) setCode(s.profile.default_writing_config_code);
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async (p: any) => {
    setMsg('');
    try { await api.put('/admin/accounting/ebalance/profile', p); setMsg('Профайл хадгалагдлаа.'); await load(); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };
  const [diagLoading, setDiagLoading] = useState(false);
  const runDiagnose = async () => {
    setMsg(''); setDiag(null); setDiagLoading(true);
    try { setDiag(await api.post('/admin/accounting/ebalance/diagnose')); await load(); }
    catch (e: any) { setMsg('Оношилгоо: ' + (e?.message || e)); }
    finally { setDiagLoading(false); }
  };
  const setMap = async (id: string, val: string) => {
    try { await api.put(`/admin/accounting/ebalance/account-map/${id}`, { ebalance_map_code: val }); await load(); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };
  const runPreview = async () => {
    setMsg(''); setPreview(null);
    try { setPreview(await api.post(`/admin/accounting/ebalance/preview?as_of=${asOf}`)); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };
  const submit = async (dry: boolean) => {
    setMsg('');
    if (!code) { setMsg('Тайлант үеийн код оруулна уу (жиш. End_2026_H_2)'); return; }
    try {
      const r = await api.post('/admin/accounting/ebalance/submit', { writing_config_code: code, as_of: asOf, dry_run: dry });
      setMsg(dry ? `Бэлтгэлээ: ${r.cell_count} нүд (dry-run, илгээгээгүй).` : `Илгээлээ: ${r.status}`);
      await load();
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  if (!st) return <p>Ачааллаж байна…</p>;

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>E-Balance — Сангийн яамны цахим санхүүгийн тайлан</h3>
      <div style={{ ...noticeBox, background: st.can_submit ? '#ecfdf5' : '#fffbeb', color: st.can_submit ? '#065f46' : '#92400e' }}>
        {st.configured
          ? `✓ Серверийн холболт тохируулагдсан (орчин: ${st.env}, ${st.grant_type}). Тайлан илгээх боломжтой.`
          : st.can_submit
            ? `✓ Туршилтын орчинтой (${st.env}) шууд холбогдож байна — токен шаардахгүй. Тайлант үеийн жагсаалт татаж, dry-run/илгээлт туршиж болно.`
            : `⚠ Серверийн E-Balance холболт хараахан хийгдээгүй. Профайл, нүдний холболт, dry-run хийж болно.`}
      </div>
      {msg && <div style={noticeBox}>{msg}</div>}

      {/* Профайл */}
      <ProfileForm profile={st.profile} onSave={saveProfile} />

      {/* Гинжин оношилгоо */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>Гинжин оношилгоо (эрх → холбогдсон үе → хүсэлт)</div>
          <button style={smallBtn} onClick={runDiagnose} disabled={!st.profile?.org_reg_no || diagLoading}>{diagLoading ? 'Оношилж байна…' : 'Оношлох'}</button>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Профайлын РД-аар 2→3→4 сервисийг лавлаж, тайлан илгээхэд юу бэлэн болохыг шалгана (юу ч илгээхгүй).</div>
        {diagLoading && <div style={noticeBox}>Сервер E-Balance-аас лавлаж байна… (токен авах + 3 сервис, 10-20 сек)</div>}
        {diag && (
          <div>
            {diag.steps.map((s: any, i: number) => (
              <div key={i} style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 6, background: s.ok ? '#ecfdf5' : '#fef2f2', color: s.ok ? '#065f46' : '#b91c1c' }}>
                <div style={{ fontWeight: 600 }}>{s.ok ? '✓' : '✗'} {s.step}</div>
                <div style={{ fontSize: 13 }}>{s.detail}</div>
                {Array.isArray(s.data) && s.data.length > 0 && (
                  <div style={{ fontSize: 12, marginTop: 4, color: '#334155' }}>
                    {s.data.map((d: any, j: number) => (
                      <div key={j}>• {d.code ? `${d.code} · ${d.name} — ${d.status}${d.linked ? ' ✓' : ''}` : JSON.stringify(d)}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div style={{ ...resultBox, marginTop: 6, background: diag.ready ? '#ecfdf5' : '#fffbeb', color: diag.ready ? '#065f46' : '#92400e' }}>
              {diag.ready
                ? `✓ Тайлан илгээхэд бэлэн (тайлант үе: ${diag.target_writing_config_code}). Нүдний холболтоо хийгээд Илгээх дарна уу.`
                : '⚠ Бүрэн бэлэн болоогүй — дээрх алхмуудаас улаанаар тэмдэглэснийг шийднэ үү.'}
            </div>
          </div>
        )}
      </div>

      {/* Preview + илгээх */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Тайлан бэлтгэх / илгээх</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <button style={smallBtn} onClick={loadConfigs}>E-Balance-аас тайлант үе татах</button>
          {configs.length > 0 ? (
            <select value={code} onChange={e => setCode(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto' }}>
              <option value="">— тайлант үе сонгох —</option>
              {configs.map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
            </select>
          ) : (
            <label style={{ fontSize: 13 }}>Тайлант үе <input value={code} onChange={e => setCode(e.target.value)} placeholder="End_2026_H_2" style={{ ...input, marginTop: 0, width: 200, display: 'inline-block' }} /></label>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Тайлагнах өдөр <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }} /></label>
          <button style={smallBtn} onClick={runPreview}>Preview (нүд бэлтгэх)</button>
          <button style={smallBtn} onClick={() => submit(true)}>Dry-run</button>
          <button style={primaryBtn} onClick={() => submit(false)} disabled={!st.can_submit}>Илгээх</button>
        </div>
        {preview && (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 6 }}>{preview.cell_count} нүд бэлэн. {preview.note}</div>
            {preview.cells.length > 0 && (
              <table style={table}>
                <thead><tr><th style={th}>Нүд ID</th><th style={th}>Данс</th><th style={{ ...th, textAlign: 'right' }}>Дүн</th></tr></thead>
                <tbody>
                  {preview.cells.map((c: any) => (
                    <tr key={c.cell_id}><td style={td}>{c.cell_id}</td><td style={td}>{c.account_code} · {c.account_name}</td><td style={{ ...td, textAlign: 'right' }}>{fmt(c.value)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Данс → нүд холболт */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Данс ↔ E-Balance нүдний холболт ({st.mapped_accounts} холбогдсон)</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Данс бүрийг E-Balance маягтын нүдний ID-тай холбоно. Нүдний ID нь Сангийн яамны маягтаас гарна.</div>
        <table style={table}>
          <thead><tr><th style={th}>Код</th><th style={th}>Дансны нэр</th><th style={th}>Нүдний ID</th></tr></thead>
          <tbody>
            {maps.map(m => (
              <tr key={m.id}>
                <td style={td}>{m.code}</td>
                <td style={td}>{m.name}</td>
                <td style={td}>
                  <input defaultValue={m.ebalance_map_code} placeholder="—"
                    onBlur={e => { if (e.target.value !== m.ebalance_map_code) setMap(m.id, e.target.value); }}
                    style={{ ...input, marginTop: 0, width: 120 }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Илгээлтийн түүх */}
      {subs.length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Илгээлтийн түүх</div>
          <table style={table}>
            <thead><tr><th style={th}>Тайлант үе</th><th style={th}>Огноо</th><th style={th}>Нүд</th><th style={th}>Төлөв</th><th style={th}>Тэмдэглэл</th></tr></thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.id}>
                  <td style={td}>{s.writing_config_code}</td><td style={td}>{s.as_of}</td>
                  <td style={td}>{s.cell_count}</td>
                  <td style={td}><span style={{ ...badge, background: '#f1f5f9', color: '#334155' }}>{SUB_STATUS_MN[s.status] || s.status}</span></td>
                  <td style={{ ...td, fontSize: 12, color: '#64748b' }}>{s.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProfileForm({ profile, onSave }: { profile: any; onSave: (p: any) => void }) {
  const [org, setOrg] = useState(profile?.org_reg_no || '');
  const [user, setUser] = useState(profile?.user_reg_no || '');
  const [role, setRole] = useState(profile?.per_map_user_role_id || '');
  const [def, setDef] = useState(profile?.default_writing_config_code || '');
  return (
    <div style={card}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Байгууллагын профайл</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ flex: 1, minWidth: 160 }}>Байгууллагын РД<input value={org} onChange={e => setOrg(e.target.value)} style={input} /></label>
        <label style={{ flex: 1, minWidth: 160 }}>Нягтлан бодогчийн РД<input value={user} onChange={e => setUser(e.target.value)} style={input} /></label>
        <label style={{ flex: 1, minWidth: 140 }}>Эрхийн ID (сонголт)<input value={role} onChange={e => setRole(e.target.value)} style={input} /></label>
        <label style={{ flex: 1, minWidth: 160 }}>Үндсэн тайлант үе<input value={def} onChange={e => setDef(e.target.value)} placeholder="End_2026_H_2" style={input} /></label>
      </div>
      <button style={{ ...primaryBtn, marginTop: 10 }} onClick={() => onSave({ org_reg_no: org, user_reg_no: user, per_map_user_role_id: role, default_writing_config_code: def })}>Профайл хадгалах</button>
    </div>
  );
}

// ─── НӨАТ (10%) ───
function VatTab() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [msg, setMsg] = useState('');
  const now = new Date();
  const [kind, setKind] = useState<'sale' | 'purchase'>('sale');
  const [docDate, setDocDate] = useState(now.toISOString().slice(0, 10));
  const [amount, setAmount] = useState<number>(0);
  const [gross, setGross] = useState(true);
  const [cpCode, setCpCode] = useState('1110');
  const [tradeId, setTradeId] = useState('');
  const [from, setFrom] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [report, setReport] = useState<any>(null);

  const load = async () => {
    const a = (await api.get('/admin/accounting/accounts')).items || [];
    const leaves = a.filter((x: Account) => x.is_leaf && x.is_active);
    setAccounts(leaves);
    if (!tradeId) {
      const def = leaves.find((x: Account) => x.code === '5100') || leaves[0];
      if (def) setTradeId(def.id);
    }
  };
  useEffect(() => { load(); }, []);

  // НӨАТ 10% урьдчилсан тооцоо
  const net = gross ? Math.round((amount / 1.1) * 100) / 100 : amount;
  const vat = gross ? Math.round((amount - net) * 100) / 100 : Math.round(amount * 0.1 * 100) / 100;

  const tradeAccounts = accounts.filter(a =>
    kind === 'sale' ? a.account_type === 'income' : (a.account_type === 'expense' || a.account_type === 'asset'));

  const submit = async () => {
    setMsg('');
    if (!amount || !tradeId) { setMsg('Дүн болон данс сонгоно уу'); return; }
    try {
      const r = await api.post('/admin/accounting/vat', {
        kind, doc_date: docDate, amount, amount_is_gross: gross,
        counterpart_account_code: cpCode, trade_account_id: tradeId,
      });
      setMsg(`Бичигдлээ: цэвэр ${fmt(r.net)}₮ + НӨАТ ${fmt(r.vat)}₮ = нийт ${fmt(r.gross)}₮`);
      setAmount(0);
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  const runReport = async () => {
    setMsg('');
    try { setReport(await api.get(`/admin/accounting/vat/report?date_from=${from}&date_to=${to}`)); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      {msg && <div style={noticeBox}>{msg}</div>}

      {/* НӨАТ-тай гүйлгээ */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>НӨАТ-тай гүйлгээ (10%)</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Төрөл
            <select value={kind} onChange={e => setKind(e.target.value as any)} style={{ ...input, marginTop: 4, width: 'auto' }}>
              <option value="sale">Борлуулалт (НӨАТ өглөг)</option>
              <option value="purchase">Худалдан авалт (НӨАТ авлага)</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>Огноо<input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} style={{ ...input, marginTop: 4 }} /></label>
          <label style={{ fontSize: 13 }}>Дүн (₮)<input type="number" value={amount || ''} onChange={e => setAmount(Number(e.target.value))} style={{ ...input, marginTop: 4, width: 140 }} /></label>
          <label style={{ fontSize: 13, alignSelf: 'end', display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
            <input type="checkbox" checked={gross} onChange={e => setGross(e.target.checked)} /> НӨАТ багтсан
          </label>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <label style={{ fontSize: 13 }}>{kind === 'sale' ? 'Мөнгө/авлага' : 'Мөнгө/өглөг'} данс
            <select value={cpCode} onChange={e => setCpCode(e.target.value)} style={{ ...input, marginTop: 4, width: 'auto' }}>
              <option value="1110">1110 · Касс</option>
              <option value="1120">1120 · Харилцах данс</option>
              <option value={kind === 'sale' ? '1210' : '3100'}>{kind === 'sale' ? '1210 · Авлага' : '3100 · Өглөг'}</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>{kind === 'sale' ? 'Орлого' : 'Зардал/хөрөнгө'} данс
            <select value={tradeId} onChange={e => setTradeId(e.target.value)} style={{ ...input, marginTop: 4, width: 'auto' }}>
              {tradeAccounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </label>
        </div>
        <div style={{ marginTop: 10, color: '#236844', fontWeight: 600 }}>
          Цэвэр {fmt(net)}₮ + НӨАТ {fmt(vat)}₮ = нийт {fmt(net + vat)}₮
        </div>
        <button style={{ ...primaryBtn, marginTop: 10 }} onClick={submit}>Гүйлгээ бичих</button>
      </div>

      {/* НӨАТ тайлан */}
      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>НӨАТ тайлан (төлөх дүн)</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Эхлэх<input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...input, marginTop: 0, display: 'inline-block', width: 'auto' }} /></label>
          <label style={{ fontSize: 13 }}>Дуусах<input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...input, marginTop: 0, display: 'inline-block', width: 'auto' }} /></label>
          <button style={smallBtn} onClick={runReport}>Тайлан гаргах</button>
        </div>
        {report && (
          <table style={{ ...table, marginTop: 12 }}>
            <tbody>
              <tr><td style={td}>Борлуулалтын НӨАТ (өглөг)</td><td style={{ ...td, textAlign: 'right' }}>{fmt(report.output_vat)}</td></tr>
              <tr><td style={td}>Худалдан авалтын НӨАТ (авлага)</td><td style={{ ...td, textAlign: 'right' }}>{fmt(report.input_vat)}</td></tr>
              <tr><td style={{ ...td, fontWeight: 700 }}>{report.note}</td><td style={{ ...td, textAlign: 'right', fontWeight: 700, color: report.payable >= 0 ? '#b45309' : '#166534' }}>{fmt(Math.abs(report.payable))}</td></tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Харилцагч (авлага/өглөгийн дэд дэвтэр) ───
interface Partner { id: string; name: string; partner_type: string; phone: string; reg_no: string; }

function PartnersTab() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [acctId, setAcctId] = useState('');
  const [balances, setBalances] = useState<any>(null);
  const [ledger, setLedger] = useState<{ pid: string; name: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [newName, setNewName] = useState('');

  const load = async () => {
    const [p, a] = await Promise.all([
      api.get('/admin/accounting/partners'),
      api.get('/admin/accounting/accounts'),
    ]);
    setPartners(p.items || []);
    const leaves = (a.items || []).filter((x: Account) => x.is_leaf);
    setAccounts(leaves);
    // Default: авлагын данс (1210) эсвэл эхнийх
    if (!acctId) {
      const ar = leaves.find((x: Account) => x.code === '1210') || leaves[0];
      if (ar) setAcctId(ar.id);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (acctId) api.get(`/admin/accounting/partners/balances?account_id=${acctId}`).then(setBalances).catch(() => setBalances(null));
  }, [acctId, partners.length]);

  const importUnits = async () => {
    setMsg('');
    try { const r = await api.post('/admin/accounting/partners/import-units'); setMsg(r.message); await load(); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };
  const addPartner = async () => {
    if (!newName.trim()) return;
    try { await api.post('/admin/accounting/partners', { name: newName }); setNewName(''); await load(); }
    catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      {msg && <div style={noticeBox}>{msg}</div>}
      {ledger && <PartnerLedgerModal pid={ledger.pid} name={ledger.name} accountId={acctId} onClose={() => setLedger(null)} />}

      {/* Дэд дэвтэр — данс сонгож харилцагчаар задлах */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Авлага/өглөгийн тооцоо (харилцагчаар)</div>
          <label style={{ fontSize: 13 }}>Данс{' '}
            <select value={acctId} onChange={e => setAcctId(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </label>
        </div>
        <table style={table}>
          <thead><tr>
            <th style={th}>Харилцагч</th>
            <th style={{ ...th, textAlign: 'right' }}>Дебет</th>
            <th style={{ ...th, textAlign: 'right' }}>Кредит</th>
            <th style={{ ...th, textAlign: 'right' }}>Үлдэгдэл</th>
            <th style={th}></th>
          </tr></thead>
          <tbody>
            {(balances?.items || []).map((b: any, i: number) => (
              <tr key={i}>
                <td style={td}>{b.name}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(b.debit)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmt(b.credit)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: b.balance > 0 ? '#b91c1c' : '#166534' }}>{fmt(b.balance)}</td>
                <td style={td}>{b.partner_id && <button style={smallBtn} onClick={() => setLedger({ pid: b.partner_id, name: b.name })}>Дэвтэр</button>}</td>
              </tr>
            ))}
            {(!balances || balances.items.length === 0) && <tr><td style={td} colSpan={5}>Тухайн дансанд харилцагчийн гүйлгээ алга.</td></tr>}
          </tbody>
          {balances && balances.items.length > 0 && (
            <tfoot><tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={3}>Нийт үлдэгдэл</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(balances.total_balance)}</td><td style={td}></td>
            </tr></tfoot>
          )}
        </table>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Улаан = авлагатай (өр төлөх ёстой), ногоон = илүү төлсөн/өглөг. Журнал бичихдээ мөрөнд харилцагч зааж холбоно.</div>
      </div>

      {/* Харилцагчийн жагсаалт */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Харилцагчид ({partners.length})</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Шинэ харилцагчийн нэр" style={{ ...input, marginTop: 0, width: 200 }} />
            <button style={smallBtn} onClick={addPartner}>+ Нэмэх</button>
            <button style={primaryBtn} onClick={importUnits}>Айл өрхөөс импорт</button>
          </div>
        </div>
        <table style={table}>
          <thead><tr><th style={th}>Нэр</th><th style={th}>Төрөл</th><th style={th}>Утас</th></tr></thead>
          <tbody>
            {partners.map(p => (
              <tr key={p.id}><td style={td}>{p.name}</td>
                <td style={td}>{p.partner_type === 'resident' ? 'Оршин суугч' : p.partner_type === 'supplier' ? 'Нийлүүлэгч' : p.partner_type === 'employee' ? 'Ажилтан' : 'Бусад'}</td>
                <td style={td}>{p.phone || '—'}</td></tr>
            ))}
            {partners.length === 0 && <tr><td style={td} colSpan={3}>Харилцагч алга. «Айл өрхөөс импорт» дарж оршин суугчдыг татна уу.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PartnerLedgerModal({ pid, name, accountId, onClose }: { pid: string; name: string; accountId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    api.get(`/admin/accounting/partners/${pid}/ledger?account_id=${accountId}`).then(setData).catch(() => setData({ lines: [] }));
  }, [pid, accountId]);
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBox, width: 680 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Харилцагчийн дэвтэр — {name}</h3>
          <button style={smallBtn} onClick={onClose}>Хаах</button>
        </div>
        {!data ? <p>Ачааллаж байна…</p> : (
          <table style={{ ...table, marginTop: 12 }}>
            <thead><tr><th style={th}>Огноо</th><th style={th}>Дугаар</th><th style={th}>Утга</th>
              <th style={{ ...th, textAlign: 'right' }}>Дебет</th><th style={{ ...th, textAlign: 'right' }}>Кредит</th><th style={{ ...th, textAlign: 'right' }}>Үлдэгдэл</th></tr></thead>
            <tbody>
              {(data.lines || []).map((l: any, i: number) => (
                <tr key={i}><td style={td}>{l.doc_date}</td><td style={td}>{l.doc_no}</td><td style={td}>{l.description}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{l.debit ? fmt(l.debit) : ''}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{l.credit ? fmt(l.credit) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(l.balance)}</td></tr>
              ))}
              {(data.lines || []).length === 0 && <tr><td style={td} colSpan={6}>Гүйлгээ алга.</td></tr>}
            </tbody>
            <tfoot><tr><td style={{ ...td, fontWeight: 700 }} colSpan={5}>Эцсийн үлдэгдэл</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.closing_balance)}</td></tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Нээлт/Хаалт (эхний үлдэгдэл + жил хаах) ───
function OpenCloseTab() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [bal, setBal] = useState<Record<string, { debit: number; credit: number }>>({});
  const [docDate, setDocDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [msg, setMsg] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  const load = async () => {
    const acc = (await api.get('/admin/accounting/accounts')).items || [];
    const leaves = acc.filter((a: Account) => a.is_leaf && a.is_active);
    setAccounts(leaves);
    const ob = await api.get('/admin/accounting/opening-balances');
    const m: any = {};
    (ob.lines || []).forEach((l: any) => { m[l.account_id] = { debit: l.debit, credit: l.credit }; });
    setBal(m);
    if (ob.doc_date) setDocDate(ob.doc_date);
  };
  useEffect(() => { load(); }, []);

  const totD = Object.values(bal).reduce((s, v) => s + (Number(v.debit) || 0), 0);
  const totC = Object.values(bal).reduce((s, v) => s + (Number(v.credit) || 0), 0);
  const balanced = totD === totC && totD > 0;

  const setCell = (id: string, side: 'debit' | 'credit', val: number) =>
    setBal(b => ({ ...b, [id]: { debit: side === 'debit' ? val : 0, credit: side === 'credit' ? val : 0 } }));

  const saveOpening = async () => {
    setMsg('');
    const lines = Object.entries(bal).filter(([, v]) => v.debit || v.credit)
      .map(([account_id, v]) => ({ account_id, debit: Number(v.debit) || 0, credit: Number(v.credit) || 0 }));
    try {
      await api.post('/admin/accounting/opening-balances', { doc_date: docDate, lines });
      setMsg('Нээлтийн үлдэгдэл хадгалагдлаа.');
      await load();
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  const closeYear = async () => {
    if (!window.confirm(`${year} оныг хаах уу? Орлого/зардал хуримтлагдсан үр дүнд шилжинэ.`)) return;
    setMsg('');
    try {
      const r = await api.post(`/admin/accounting/close-period?year=${year}`);
      setMsg(`${year} он хаагдлаа. Цэвэр үр дүн: ${fmt(r.net_result)}₮ (${r.net_result >= 0 ? 'ашиг' : 'алдагдал'}).`);
    } catch (e: any) { setMsg('Алдаа: ' + (e?.message || e)); }
  };

  return (
    <div>
      {msg && <div style={noticeBox}>{msg}</div>}

      {/* Эхний үлдэгдэл */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Эхний (нээлтийн) үлдэгдэл</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13 }}>Огноо <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} style={{ ...input, marginTop: 0, width: 'auto', display: 'inline-block' }} /></label>
            <button style={primaryBtn} onClick={saveOpening} disabled={!balanced}>Хадгалах</button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>СӨХ системд шилжихдээ данс бүрийн эхлэлийн үлдэгдлийг оруулна. Дебет=Кредит байх ёстой.</div>
        <div style={{ maxHeight: 340, overflowY: 'auto' }}>
          <table style={table}>
            <thead><tr><th style={th}>Код</th><th style={th}>Данс</th><th style={{ ...th, textAlign: 'right' }}>Дебет</th><th style={{ ...th, textAlign: 'right' }}>Кредит</th></tr></thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id}>
                  <td style={td}>{a.code}</td><td style={td}>{a.name}</td>
                  <td style={td}><input type="number" value={bal[a.id]?.debit || ''} onChange={e => setCell(a.id, 'debit', Number(e.target.value))} style={{ ...input, marginTop: 0, textAlign: 'right' }} /></td>
                  <td style={td}><input type="number" value={bal[a.id]?.credit || ''} onChange={e => setCell(a.id, 'credit', Number(e.target.value))} style={{ ...input, marginTop: 0, textAlign: 'right' }} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={2}>Нийт</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(totD)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(totC)}</td>
            </tr></tfoot>
          </table>
        </div>
        <div style={{ marginTop: 8, fontWeight: 600, color: balanced ? '#166534' : '#b91c1c' }}>
          {balanced ? '✓ Дебет ба кредит тэнцсэн' : totD || totC ? `Зөрүү: ${fmt(Math.abs(totD - totC))}` : 'Үлдэгдэл оруулна уу'}
        </div>
      </div>

      {/* Жил хаах */}
      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Жилийн эцсийн хаалт</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Тухайн оны бүх орлого/зардлын дансдыг хааж, цэвэр үр дүнг «Хуримтлагдсан үр дүн» (3510) руу шилжүүлнэ. Нэг жилд нэг удаа.</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...input, marginTop: 0, width: 'auto' }}>
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y} он</option>)}
          </select>
          <button style={{ ...primaryBtn, background: '#b45309' }} onClick={closeYear}>Жил хаах</button>
        </div>
      </div>
    </div>
  );
}

// ─── Дансны хуулга (modal) ───
function AccountLedgerModal({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  useEffect(() => {
    api.get(`/admin/accounting/accounts/${accountId}/ledger`).then(setData).catch(() => setData({ lines: [] }));
  }, [accountId]);

  // И-баримтын багана зөвхөн ОРЛОГЫН данс (5100 гэх мэт)-д харагдана.
  const showEbarimt = data?.account?.type === 'income';

  const toggleEbarimt = async (line: any, issued: boolean) => {
    // Оптимист шинэчлэл
    setData((d: any) => ({ ...d, lines: d.lines.map((x: any) =>
      x.line_id === line.line_id ? { ...x, ebarimt_issued: issued } : x) }));
    try {
      await api.patch(`/admin/accounting/journal-lines/${line.line_id}/ebarimt`, { issued });
    } catch {
      // Амжилтгүй бол буцаана
      setData((d: any) => ({ ...d, lines: d.lines.map((x: any) =>
        x.line_id === line.line_id ? { ...x, ebarimt_issued: !issued } : x) }));
      alert('И-баримтын тэмдэглэгээ хадгалж чадсангүй.');
    }
  };

  const allLines = data?.lines || [];
  const shown = showEbarimt && onlyMissing ? allLines.filter((l: any) => !l.ebarimt_issued) : allLines;
  const issuedCnt = showEbarimt ? allLines.filter((l: any) => l.ebarimt_issued).length : 0;
  const colCount = showEbarimt ? 7 : 6;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBox, width: showEbarimt ? 860 : 720 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Дансны хуулга{data?.account ? ` — ${data.account.code} · ${data.account.name}` : ''}</h3>
          <button style={smallBtn} onClick={onClose}>Хаах</button>
        </div>
        {showEbarimt && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ ...badge, background: '#ecfdf5', color: '#065f46' }}>
              И-баримт шивсэн: {issuedCnt}/{allLines.length}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} />
              Зөвхөн шивэгдээгүйг харах
            </label>
          </div>
        )}
        {!data ? <p>Ачааллаж байна…</p> : (
          <table style={{ ...table, marginTop: 12 }}>
            <thead><tr>
              <th style={th}>Огноо</th><th style={th}>Дугаар</th><th style={th}>Утга</th>
              <th style={{ ...th, textAlign: 'right' }}>Дебет</th><th style={{ ...th, textAlign: 'right' }}>Кредит</th>
              <th style={{ ...th, textAlign: 'right' }}>Үлдэгдэл</th>
              {showEbarimt && <th style={{ ...th, textAlign: 'center' }}>И-баримт</th>}
            </tr></thead>
            <tbody>
              {!onlyMissing && <tr><td style={{ ...td, color: '#64748b' }} colSpan={5}>Эхний үлдэгдэл</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(data.opening_balance)}</td>{showEbarimt && <td style={td}></td>}</tr>}
              {shown.map((l: any, i: number) => (
                <tr key={l.line_id || i} style={showEbarimt && !l.ebarimt_issued ? { background: '#fffbeb' } : undefined}>
                  <td style={td}>{l.doc_date}</td><td style={td}>{l.doc_no}</td><td style={td}>{l.description}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{l.debit ? fmt(l.debit) : ''}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{l.credit ? fmt(l.credit) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(l.balance)}</td>
                  {showEbarimt && (
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!l.ebarimt_issued}
                        onChange={e => toggleEbarimt(l, e.target.checked)}
                        title="Оршин суугчид и-баримт шивэгдсэн эсэх"
                        style={{ width: 17, height: 17, cursor: 'pointer' }} />
                    </td>
                  )}
                </tr>
              ))}
              {shown.length === 0 && <tr><td style={td} colSpan={colCount}>{onlyMissing ? 'Бүх гүйлгээнд и-баримт шивэгдсэн байна. ✓' : 'Гүйлгээ алга.'}</td></tr>}
            </tbody>
            <tfoot><tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={3}>Эцсийн үлдэгдэл</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.total_debit)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.total_credit)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(data.closing_balance)}</td>
              {showEbarimt && <td style={td}></td>}
            </tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── styles ───
const headerBar: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#236844', padding: '0 20px', height: 56 };
const logoBox: React.CSSProperties = { width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const tabBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#fff', padding: '18px 16px', cursor: 'pointer', fontSize: 14 };
const versionBadge: React.CSSProperties = { color: 'rgba(255,255,255,.85)', fontSize: 13 };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.08)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' };
const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #f1f5f9' };
const primaryBtn: React.CSSProperties = { background: '#236844', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontWeight: 600 };
const smallBtn: React.CSSProperties = { background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13 };
const badge: React.CSSProperties = { padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 };
const noticeBox: React.CSSProperties = { background: '#ecfdf5', color: '#065f46', padding: '10px 14px', borderRadius: 8, marginBottom: 12 };
const emptyBox: React.CSSProperties = { background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: 24, color: '#64748b', textAlign: 'center' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, marginTop: 4, boxSizing: 'border-box' };
const modalOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const modalBox: React.CSSProperties = { background: '#fff', borderRadius: 12, padding: 24, width: 720, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' };
const resultBox: React.CSSProperties = { padding: '12px 16px', borderRadius: 8, fontWeight: 700, fontSize: 15 };
const card: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,.08)' };
